import type { QueryClient } from "@tanstack/react-query";

import type { NoteDetail, NoteTemplateTableItem } from "@/types/notes";

import { noteQueryKeys } from "./note-query";

/**
 * Refetch every cached note (detail or revision) written on this template.
 * The server trims a note's embedded template to what that note renders, so a
 * template response cannot be patched in; only a fresh read gets it right.
 * Call it only once the read replica has caught up: an open note refetches
 * immediately, and a stale read clears the invalidation it was answering.
 */
export function invalidateNotesUsingTemplate(
  queryClient: QueryClient,
  templateId: string,
) {
  return queryClient.invalidateQueries({
    queryKey: noteQueryKeys.all,
    predicate: (query) =>
      (query.state.data as NoteDetail | null | undefined)?.template?.id ===
      templateId,
  });
}

/** Cancel reads that could overwrite an authoritative mutation response. */
export async function cancelNoteTemplateCacheReads(
  queryClient: QueryClient,
  templateIds: string[] = [],
) {
  await Promise.all([
    queryClient.cancelQueries({
      queryKey: noteQueryKeys.templates(),
      exact: true,
    }),
    ...templateIds.map((templateId) =>
      queryClient.cancelQueries({
        queryKey: noteQueryKeys.templateDetail(templateId),
        exact: true,
      }),
    ),
  ]);
}

/**
 * Canonical note-template list upsert used by local mutations and realtime
 * events. It preserves viewer-specific favorite state and endpoint name order.
 */
export function upsertNoteTemplateList(
  cached: NoteTemplateTableItem[] | null | undefined,
  incoming: NoteTemplateTableItem,
): NoteTemplateTableItem[] | null | undefined {
  if (!cached) return cached;
  const existing = cached.find((template) => template.id === incoming.id);
  if (
    existing &&
    (existing.mutationRevision ?? 0) > (incoming.mutationRevision ?? 0)
  ) {
    return cached;
  }
  const patched: NoteTemplateTableItem = {
    ...incoming,
    isFavorited: incoming.isFavorited ?? existing?.isFavorited ?? false,
  };
  return [
    ...cached.filter((template) => template.id !== incoming.id),
    patched,
  ].sort((a, b) => a.name.localeCompare(b.name));
}
